import { forwardRef, useMemo } from 'react'
import { HStack, Button, Text, Box, Flex } from '@chakra-ui/react'
import type { MetaData, PaginationLink } from './types'
import { useThemeColors } from '@/hooks/useThemeColors'

const MAX_PAGE_BUTTONS = 5

interface PaginationProps {
  metas: MetaData
  onClick: (url: string | null) => void
  className?: string
}

const cleanLabel = (s: string) => s.replace(/&[^;]+;/g, '').trim()

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(({ metas, onClick }, ref) => {
  const colors = useThemeColors()
  const { total, links, per_page: perPage, current_page: currentPage } = metas

  const textColor = colors.text
  const scrollbarThumb = colors.progressBg
  const scrollbarThumbHover = colors.textMuted
  const ellipsisColor = colors.text

  const goToPage = (url: string | null) => {
    if (url) onClick(url)
  }

  const getPageInfo = () => {
    const start = (currentPage - 1) * perPage + 1
    const end = Math.min(currentPage * perPage, total)
    return `Showing ${start} to ${end} of ${total.toLocaleString()} entries`
  }

  const displayLinks = useMemo(() => {
    if (!links?.length) return []
    const lastPage = Math.max(1, Math.ceil(total / perPage))
    const pageMap = new Map<number, PaginationLink>()
    links.forEach((link) => {
      const label = cleanLabel(link.label)
      if (/^\d+$/.test(label)) pageMap.set(parseInt(label, 10), link)
    })
    const prevLink = links[0] && !/^\d+$/.test(cleanLabel(links[0].label)) ? links[0] : null
    const nextLink = links[links.length - 1] && !/^\d+$/.test(cleanLabel(links[links.length - 1].label)) ? links[links.length - 1] : null

    const result: PaginationLink[] = []
    if (prevLink) result.push(prevLink)

    if (lastPage <= MAX_PAGE_BUTTONS) {
      for (let p = 1; p <= lastPage; p++) {
        result.push(pageMap.get(p) ?? { url: null, label: String(p), active: p === currentPage })
      }
    } else {
      const start = Math.max(1, Math.min(currentPage - 2, lastPage - 4))
      const end = Math.min(lastPage, start + 4)
      if (start > 2) {
        result.push(pageMap.get(1) ?? { url: null, label: '1', active: false })
        result.push({ url: null, label: '...', active: false })
      }
      for (let p = start; p <= end; p++) {
        result.push(pageMap.get(p) ?? { url: null, label: String(p), active: p === currentPage })
      }
      if (end < lastPage - 1) {
        result.push({ url: null, label: '...', active: false })
        result.push(pageMap.get(lastPage) ?? { url: null, label: String(lastPage), active: false })
      }
    }

    if (nextLink) result.push(nextLink)
    return result
  }, [links, total, perPage, currentPage])

  return (
    <Box ref={ref} w="100%" overflowX="auto">
      <Flex direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'stretch', md: 'center' }} gap={2}>
        <Text fontSize="xs" color={textColor} whiteSpace="nowrap" mb={{ base: 1, md: 0 }} textAlign={{ base: 'center', md: 'left' }}>
          {getPageInfo()}
        </Text>

        <Box
          overflowX="auto"
          w={{ base: '100%', md: 'auto' }}
          css={{
            '&::-webkit-scrollbar': {
              height: '4px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: scrollbarThumb,
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: scrollbarThumbHover,
            },
          }}
        >
          <HStack spacing={1} justify={{ base: 'center', md: 'flex-end' }} minW="fit-content">
            {displayLinks.map((link: PaginationLink, index: number) => {
              const isDisabled = !link.url
              const isActive = link.active
              const isEllipsis = link.label === '...'

              if (isEllipsis) {
                return (
                  <Box key={`ellipsis-${index}`} px={{ base: 1, md: 2 }} py={1}>
                    <Text fontSize={{ base: 'xs', md: 'sm' }} color={ellipsisColor}>
                      …
                    </Text>
                  </Box>
                )
              }

              return (
                <Button
                  key={`${cleanLabel(link.label)}-${index}`}
                  size={{ base: 'xs', md: 'sm' }}
                  variant={isActive ? 'solid' : 'outline'}
                  colorScheme={isActive ? 'blue' : 'gray'}
                  isDisabled={isDisabled}
                  onClick={() => goToPage(link.url)}
                  minW={{ base: '32px', md: '40px' }}
                  h={{ base: '28px', md: '32px' }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  px={{ base: 2, md: 3 }}
                  flexShrink={0}
                >
                  {cleanLabel(link.label)}
                </Button>
              )
            })}
          </HStack>
        </Box>
      </Flex>
    </Box>
  )
})

Pagination.displayName = 'Pagination'


